import { Component, inject, Input, OnInit, OnDestroy } from '@angular/core';
import {
    AbstractControl,
    UntypedFormBuilder,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { cloudProviders } from 'src/app/enums/cloudprovider';
import { hostsConfig } from 'src/app/enums/hosts';
import { WbceServiceManagerService } from 'src/app/service/wbce-service-manager.service';
import { WorkspaceManagerService } from 'src/app/service/workspace-manager.service';

export enum accountStrategies {
    USE_EXISTING_ACCOUNT,
    CREATE_ACCOUNT,
}

@Component({
    selector: 'app-wbce-aws-context-account-selection',
    templateUrl: './aws-context-account-selection.component.html',
    styleUrls: ['./aws-context-account-selection.component.scss'],
    standalone: false
})
export class AwsContextAccountSelectionComponent implements OnInit, OnDestroy {
    accountStrategies = accountStrategies;
    accountStrategiesOptions = [
        {
            value: accountStrategies.CREATE_ACCOUNT,
            label: 'Create a new account',
        },
        {
            value: accountStrategies.USE_EXISTING_ACCOUNT,
            label: 'Use an existing account',
        },
    ];

    currentAccountStrategy = accountStrategies.CREATE_ACCOUNT;
    regions: {
        code: string;
        name: string;
    }[];

    private formBuilder = inject(UntypedFormBuilder);

    formGroup: UntypedFormGroup;

    @Input() props: {
        onValid: (isValid: boolean) => void;
    };

    @Input() data: {
        accountId?: string;
        accountEmail?: string;
        region: string;
    };

    subs: Subscription[] = [];

    private workspaceService = inject(WorkspaceManagerService);
    private wbceService = inject(WbceServiceManagerService);

    constructor() {
        this.formGroup = this.formBuilder.group({
            accountStrategy: [this.currentAccountStrategy],
            accountInfo: [
                '',
                [Validators.required, this.validateAccountInfo.bind(this)()],
            ],
            region: [],
        });
    }

    validateAccountInfo(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value;

            if (!value) return null;

            return this.currentAccountStrategy ===
                accountStrategies.CREATE_ACCOUNT
                ? Validators.email(control)
                : Validators.pattern(/\d{12}/)(control);
        };
    }

    ngOnInit(): void {
        this.subs.push(
            this.formGroup.controls.accountStrategy.valueChanges.subscribe(
                this.onAccountStrategyChanged.bind(this)
            ),
            this.formGroup.controls.accountInfo.valueChanges.subscribe(
                this.onAccountInfoChanged.bind(this)
            ),
            this.formGroup.controls.region.valueChanges.subscribe(
                this.onRegionChanged.bind(this)
            )
        );

        const providerRegions = hostsConfig[cloudProviders.AWS].region;

        // use region from project if it exists or wbce project
        const wbceRegion = providerRegions.find(
            ({ code }) =>
                code ===
                (this.wbceService.currentProject?.cloudEnv.region ??
                    this.workspaceService.wbceProject?.cloudEnv.region)
        );

        this.regions = wbceRegion
            ? providerRegions
            : [{ name: wbceRegion.name, code: wbceRegion.code }];

        this.formGroup.controls.region.setValue(wbceRegion.code);
    }

    ngOnDestroy() {
        this.subs.forEach((s) => s.unsubscribe());
    }

    onAccountStrategyChanged(value: number) {
        this.currentAccountStrategy = value;
        this.formGroup.controls.accountInfo.setValue('');
    }

    onAccountInfoChanged(value: string) {
        if (this.currentAccountStrategy === accountStrategies.CREATE_ACCOUNT) {
            this.data.accountEmail = value;
            delete this.data.accountId;
        } else {
            this.data.accountId = value;
            delete this.data.accountEmail;
        }
        this.props.onValid(this.formGroup.controls.accountInfo.valid);
    }

    onRegionChanged(value: string) {
        this.data.region = value;
    }
}
